import { useState, useMemo, useEffect, useRef } from 'react';
import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import {
  ExpensesReportFilterInput,
  IncidentCategoryOption,
  OrderDirectionOption,
  PageSizeOption,
  useGetExpensesReportQuery,
} from '@/graphql/generated';
import {
  usePropertyDetailContext,
  useErrorHandlerContext,
  useUserLanguage,
  useAddExpenseFormModalContext,
} from '@/customHooks';
import { incidentCategoryMapper, orderDirectionMapper, advancedNormalizeDate } from '@/helpers';
import { Button, LoadingSpinner, RentChargeStatusOptionPill } from '../atoms';
import { NormalFormSelectionField, Pagination } from '../molecules';
import textsData from '@/textData/PropertyDetailTexts.json';

const FiltersWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 2fr 1fr 1fr 1fr;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;

  & > div {
    padding: 15px;
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: solid 1px lightgray;
    background-color: ${({ theme }) => theme.colors.light};
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`;

const INITIAL_FILTERS: ExpensesReportFilterInput = {
  categoryOption: null,
  orderDirection: OrderDirectionOption.Desc,
  pageSize: PageSizeOption.TwentyFive,
  page: '1',
};


const formatAmount = (amount: string, currency: string, userLanguage: 'es' | 'en') => {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return amount;
  return new Intl.NumberFormat(userLanguage === 'es' ? 'es-ES' : 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(numAmount);
};

export const PropertyDetailExpensesList = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.expensesList : textsData.en.expensesList;

  const { property } = usePropertyDetailContext();
  const { open: errorHandle } = useErrorHandlerContext();
  const { openModal } = useAddExpenseFormModalContext();

  const [filters, setFilters] = useState<ExpensesReportFilterInput>(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, error, loading, refetch } = useGetExpensesReportQuery({
    variables: {
      filterInput: {
        ...filters,
        page: currentPage.toString(),
        ...(property?.id && { propertyId: property.id }), // Solo incluir propertyId si existe
      },
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: !property?.id, // No ejecutar el query si no hay propertyId
  });

  // Refrescar cuando cambia el property del contexto
  const prevPropertyRef = useRef(property);
  useEffect(() => {
    // Si el property cambió de undefined a un valor, o si cambió el ID, o si cambió la referencia del objeto, hacer refetch
    if (property?.id) {
      const wasUndefined = prevPropertyRef.current === undefined;
      const idChanged = prevPropertyRef.current?.id !== property.id;
      const referenceChanged = prevPropertyRef.current !== property;
      
      if (wasUndefined || idChanged || referenceChanged) {
        prevPropertyRef.current = property;
        // Usar un pequeño delay para asegurar que el query no esté en estado skip
        const timeoutId = setTimeout(() => {
          refetch();
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
    prevPropertyRef.current = property;
  }, [property, refetch]);

  if (error) errorHandle(error);

  const flattenedData = useMemo(
    () =>
      (data?.expensesReport?.items ?? []).map((expense) => ({
        id: expense.id,
        category: incidentCategoryMapper(expense.typeOption, userLanguage),
        title: expense.title || '-',
        description: expense.description || '-',
        date: expense.date,
        amount: formatAmount(expense.expenseCost, expense.currencyOption, userLanguage),
        chargeStatus: expense.chargeStatus,
      })),
    [data, userLanguage]
  );

  const categoryOptions = Object.values(IncidentCategoryOption).map((value) => ({
    name: incidentCategoryMapper(value, userLanguage),
    value,
  }));

  const orderDirectionOptions = Object.values(OrderDirectionOption).map((value) => ({
    name: orderDirectionMapper(value, userLanguage),
    value,
  }));

  const handleFilterChange = (field: keyof ExpensesReportFilterInput, value: string | null) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const currentDataCount = flattenedData.length;
  const totalPages = data?.expensesReport?.pagination?.totalPages || 1;
  const hasData = currentDataCount > 0;

  return (
    <>
      <Row>
        <Col>
          <h2>{texts.title}</h2>
        </Col>
        <Col xs="content" style={{ marginTop: '1rem' }}>
          <Button onClick={() => property?.id && openModal(property.id)}>
            + {texts.createButton}
          </Button>
        </Col>
      </Row>
      <FiltersWrapper>
        <Row gutterWidth={16} align="center">
          <Col md={4} sm={12}>
            <NormalFormSelectionField<IncidentCategoryOption>
              label={texts.filters.type}
              name="category"
              options={categoryOptions}
              value={filters.categoryOption}
              onChange={(value) => handleFilterChange('categoryOption', value)}
              noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
            />
          </Col>
          <Col md={4} sm={12}>
            <NormalFormSelectionField<OrderDirectionOption>
              label={texts.filters.order}
              name="orderDirection"
              options={orderDirectionOptions}
              value={filters.orderDirection}
              onChange={(value) => handleFilterChange('orderDirection', value)}
            />
          </Col>
          <Col md={4} sm={12} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button secondary onClick={() => setFilters(INITIAL_FILTERS)}>
              {texts.clear || (userLanguage === 'es' ? 'Limpiar filtros' : 'Clear filters')}
            </Button>
          </Col>
        </Row>
      </FiltersWrapper>
      {loading ? (
        <Row justify="center">
          <Col xs="content">
            <LoadingSpinner text />
          </Col>
        </Row>
      ) : hasData ? (
        <>
          <StyledGridTable>
            <StyledGridHeader>
              <div>{texts.tableHeader.category}</div>
              <div>{texts.tableHeader.title}</div>
              <div>{texts.tableHeader.description}</div>
              <div>{texts.tableHeader.date}</div>
              <div>{texts.tableHeader.amount}</div>
              <div>{texts.tableHeader.status}</div>
            </StyledGridHeader>
            {flattenedData.map((expense) => (
              <StyledGridRow key={expense.id}>
                <div>{expense.category}</div>
                <div>{expense.title}</div>
                <div>{expense.description}</div>
                <div>{expense.date ? advancedNormalizeDate(userLanguage, new Date(expense.date).toISOString()) || '-' : '-'}</div>
                <div>{expense.amount}</div>
                <div>{expense.chargeStatus ? <RentChargeStatusOptionPill option={expense.chargeStatus} /> : '-'}</div>
              </StyledGridRow>
            ))}
          </StyledGridTable>
          <Row>
            <Col>
              <Pagination
                currentPage={currentPage}
                onPageChange={handlePageChange}
                currentDataCount={currentDataCount}
                pageSize={PageSizeOption.TwentyFive}
                totalPages={totalPages}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Row>
          <Col>
            <p>
              <b>{texts.noData}</b>
            </p>
          </Col>
        </Row>
      )}
    </>
  );
};

